import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  Wine, Camera, Plus, Search, Filter, Trash2, Calendar, MapPin,
  ChevronDown, X, Check, Store, ExternalLink, GlassWater, Sparkles,
  Image as ImageIcon, Loader2, RotateCcw, ArrowRight, FileText
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const WINE_TYPES = [
  { value: 'rouge', label: 'Rouge', color: 'bg-red-600', icon: '🍷' },
  { value: 'blanc', label: 'Blanc', color: 'bg-amber-100', icon: '🥂' },
  { value: 'rosé', label: 'Rosé', color: 'bg-pink-300', icon: '🌸' },
  { value: 'champagne', label: 'Champagne', color: 'bg-yellow-300', icon: '🍾' },
  { value: 'pétillant', label: 'Pétillant', color: 'bg-blue-200', icon: '✨' }
];

export default function WineCellarPage() {
  const navigate = useNavigate();
  const [cellar, setCellar] = useState({ wines: [], total_bottles: 0, by_type: {} });
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showScanDialog, setShowScanDialog] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [filterType, setFilterType] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedWine, setSelectedWine] = useState(null);
  const fileInputRef = useRef(null);

  const emptyWine = {
    name: '',
    domain: '',
    appellation: '',
    vintage: '',
    type: 'rouge',
    region: '',
    country: 'France',
    grape_varieties: [],
    alcohol_content: '',
    label_image: null,
    back_label_image: null,
    back_label_text: '',
    purchase_date: new Date().toISOString().split('T')[0],
    purchase_price: '',
    purchase_location: '',
    notes: '',
    quantity: 1
  };

  const [formData, setFormData] = useState(emptyWine);
  const [saving, setSaving] = useState(false);
  
  // Scan state
  const [scanStep, setScanStep] = useState(1); // 1 = front label, 2 = back label
  const [frontLabelImage, setFrontLabelImage] = useState(null);
  const [backLabelImage, setBackLabelImage] = useState(null);
  const frontInputRef = useRef(null);
  const backInputRef = useRef(null);

  useEffect(() => {
    fetchCellar();
    fetchHistory();
  }, [filterType]);

  const fetchCellar = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }

    try {
      let url = `${API_URL}/api/wine/cellar?status=in_cellar`;
      if (filterType) url += `&type=${filterType}`;
      
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        setCellar(await res.json());
      }
    } catch (error) {
      toast.error('Erreur lors du chargement de la cave');
    } finally {
      setLoading(false);
    }
  };

  const fetchHistory = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;

    try {
      const res = await fetch(`${API_URL}/api/wine/cellar/history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        setHistory(await res.json());
      }
    } catch (error) {
      console.error('Error fetching history:', error);
    }
  };

  // Handle front label image upload
  const handleFrontImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image trop grande (max 10 Mo)');
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setFrontLabelImage(reader.result);
    };
    reader.readAsDataURL(file);
  };

  // Handle back label image upload
  const handleBackImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image trop grande (max 10 Mo)');
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setBackLabelImage(reader.result);
    };
    reader.readAsDataURL(file);
  };

  // Legacy handler for add dialog
  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image trop grande (max 5 Mo)');
      return;
    }

    const reader = new FileReader();
    reader.onloadend = async () => {
      const base64 = reader.result;
      setFormData({ ...formData, label_image: base64 });
    };
    reader.readAsDataURL(file);
  };

  // Scan both labels and extract info
  const scanLabels = async () => {
    if (!frontLabelImage) {
      toast.error('Veuillez d\'abord prendre une photo de l\'étiquette');
      return;
    }

    setScanning(true);
    const token = localStorage.getItem('token');

    try {
      // Scan front label for wine info
      const frontRes = await fetch(`${API_URL}/api/wine/scan-label`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ image: frontLabelImage })
      });

      let wineInfo = {};
      if (frontRes.ok) {
        wineInfo = await frontRes.json();
      }

      // Scan back label for description if available
      let backText = '';
      if (backLabelImage) {
        const backRes = await fetch(`${API_URL}/api/wine/scan-back-label`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({ image: backLabelImage })
        });

        if (backRes.ok) {
          const backData = await backRes.json();
          backText = backData.description || '';
        }
      }

      // Update form with all extracted data
      setFormData({
        ...emptyWine,
        ...wineInfo,
        label_image: frontLabelImage,
        back_label_image: backLabelImage,
        back_label_text: backText,
        vintage: wineInfo.vintage || '',
        grape_varieties: wineInfo.grape_varieties || [],
        alcohol_content: wineInfo.alcohol_content || ''
      });

      // Close scan dialog and open add dialog
      setShowScanDialog(false);
      setShowAddDialog(true);
      resetScanState();
      
      toast.success('Étiquettes analysées avec succès !');
    } catch (error) {
      console.error('Scan error:', error);
      toast.error('Erreur lors de l\'analyse des étiquettes');
    } finally {
      setScanning(false);
    }
  };

  // Reset scan state
  const resetScanState = () => {
    setScanStep(1);
    setFrontLabelImage(null);
    setBackLabelImage(null);
  };

  // Legacy scanLabel for backward compatibility
  const scanLabel = async (imageData) => {
    setScanning(true);
    const token = localStorage.getItem('token');

    try {
      const res = await fetch(`${API_URL}/api/wine/scan-label`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ image: imageData })
      });

      if (res.ok) {
        const wineInfo = await res.json();
        setFormData({
          ...formData,
          ...wineInfo,
          label_image: imageData,
          vintage: wineInfo.vintage || '',
          grape_varieties: wineInfo.grape_varieties || [],
          alcohol_content: wineInfo.alcohol_content || ''
        });
        setShowScanDialog(false);
        setShowAddDialog(true);
        toast.success('Étiquette analysée avec succès !');
      } else {
        throw new Error('Erreur analyse');
      }
    } catch (error) {
      toast.error('Erreur lors de l\'analyse de l\'étiquette');
    } finally {
      setScanning(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    const token = localStorage.getItem('token');
    try {
      const payload = {
        ...formData,
        vintage: formData.vintage ? parseInt(formData.vintage) : null,
        alcohol_content: formData.alcohol_content ? parseFloat(formData.alcohol_content) : null,
        purchase_price: formData.purchase_price ? parseFloat(formData.purchase_price) : null,
        quantity: parseInt(formData.quantity) || 1
      };

      const res = await fetch(`${API_URL}/api/wine/cellar`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (res.ok) {
        toast.success('Bouteille ajoutée à la cave');
        setShowAddDialog(false);
        setFormData(emptyWine);
        fetchCellar();
      } else {
        throw new Error('Erreur');
      }
    } catch (error) {
      toast.error('Erreur lors de l\'ajout');
    } finally {
      setSaving(false);
    }
  };

  const consumeWine = async (wineId, quantity = 1) => {
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API_URL}/api/wine/cellar/${wineId}/consume?quantity=${quantity}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
        fetchCellar();
        fetchHistory();
        setSelectedWine(null);
      }
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const deleteWine = async (wineId) => {
    if (!window.confirm('Supprimer cette bouteille de la cave ?')) return;

    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API_URL}/api/wine/cellar/${wineId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        toast.success('Bouteille supprimée');
        fetchCellar();
        setSelectedWine(null);
      }
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const filteredWines = cellar.wines.filter(wine => {
    const matchesSearch = !searchQuery || 
      wine.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      wine.domain?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesSearch;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="px-4 py-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-charcoal flex items-center gap-2">
            <Wine className="w-7 h-7 text-red-600" />
            Ma Cave
          </h1>
          <p className="text-muted-foreground">
            {cellar.total_bottles} bouteille{cellar.total_bottles > 1 ? 's' : ''} en cave
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => { setFormData(emptyWine); setShowScanDialog(true); }}>
            <Camera className="w-4 h-4 mr-2" />
            Scanner
          </Button>
          <Button onClick={() => { setFormData(emptyWine); setShowAddDialog(true); }}>
            <Plus className="w-4 h-4 mr-2" />
            Ajouter
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-5 gap-3 mb-6">
        {WINE_TYPES.map((type) => (
          <button
            key={type.value}
            onClick={() => setFilterType(filterType === type.value ? '' : type.value)}
            className={`p-3 rounded-xl text-center transition-all ${
              filterType === type.value 
                ? 'ring-2 ring-primary bg-white shadow-md' 
                : 'bg-white hover:shadow-md'
            }`}
          >
            <span className="text-2xl block mb-1">{type.icon}</span>
            <p className="text-lg font-bold text-charcoal">{cellar.by_type[type.value] || 0}</p>
            <p className="text-xs text-muted-foreground">{type.label}</p>
          </button>
        ))}
      </div>

      <Tabs defaultValue="cellar" className="w-full">
        <TabsList className="mb-4">
          <TabsTrigger value="cellar" className="flex items-center gap-2">
            <Wine className="w-4 h-4" /> En Cave
          </TabsTrigger>
          <TabsTrigger value="history" className="flex items-center gap-2">
            <GlassWater className="w-4 h-4" /> Historique
          </TabsTrigger>
        </TabsList>

        <TabsContent value="cellar">
          {/* Search */}
          <div className="mb-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Rechercher un vin ou domaine..."
                className="pl-10"
              />
            </div>
          </div>

          {/* Wine List */}
          {filteredWines.length === 0 ? (
            <div className="bg-white rounded-xl p-12 text-center">
              <Wine className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <h3 className="font-semibold text-charcoal mb-2">Votre cave est vide</h3>
              <p className="text-muted-foreground mb-4">
                Scannez une étiquette ou ajoutez manuellement vos bouteilles.
              </p>
              <Button onClick={() => setShowScanDialog(true)}>
                <Camera className="w-4 h-4 mr-2" />
                Scanner une étiquette
              </Button>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredWines.map((wine) => {
                const typeInfo = WINE_TYPES.find(t => t.value === wine.type) || WINE_TYPES[0];
                return (
                  <div 
                    key={wine.id} 
                    className="bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow cursor-pointer"
                    onClick={() => setSelectedWine(wine)}
                  >
                    <div className="h-32 bg-gradient-to-br from-gray-100 to-gray-200 relative">
                      {wine.label_image ? (
                        <img src={wine.label_image} alt={wine.name} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <span className="text-5xl">{typeInfo.icon}</span>
                        </div>
                      )}
                      <span className={`absolute top-2 right-2 px-2 py-1 rounded-full text-xs font-medium text-white ${typeInfo.color}`}>
                        {typeInfo.label}
                      </span>
                      {wine.quantity > 1 && (
                        <span className="absolute bottom-2 right-2 bg-charcoal text-white px-2 py-1 rounded-full text-xs font-bold">
                          x{wine.quantity}
                        </span>
                      )}
                    </div>
                    <div className="p-4">
                      <h3 className="font-semibold text-charcoal line-clamp-1">{wine.name}</h3>
                      <p className="text-sm text-muted-foreground">{wine.domain}</p>
                      <div className="flex items-center justify-between mt-2">
                        <span className="text-sm text-muted-foreground">
                          {wine.vintage || 'NM'} • {wine.appellation || wine.region || 'France'}
                        </span>
                        {wine.purchase_price && (
                          <span className="text-sm font-medium text-charcoal">{wine.purchase_price}€</span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </TabsContent>

        <TabsContent value="history">
          {history.length === 0 ? (
            <div className="bg-white rounded-xl p-12 text-center">
              <GlassWater className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <h3 className="font-semibold text-charcoal mb-2">Aucune bouteille consommée</h3>
              <p className="text-muted-foreground">
                Les bouteilles que vous marquez comme consommées apparaîtront ici.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {history.map((wine) => {
                const typeInfo = WINE_TYPES.find(t => t.value === wine.type) || WINE_TYPES[0];
                return (
                  <div key={wine.id} className="bg-white rounded-xl p-4 flex items-center gap-4">
                    <div className="w-16 h-16 rounded-lg bg-gray-100 flex items-center justify-center overflow-hidden">
                      {wine.label_image ? (
                        <img src={wine.label_image} alt={wine.name} className="w-full h-full object-cover" />
                      ) : (
                        <span className="text-2xl">{typeInfo.icon}</span>
                      )}
                    </div>
                    <div className="flex-1">
                      <h4 className="font-medium text-charcoal">{wine.name}</h4>
                      <p className="text-sm text-muted-foreground">{wine.domain} • {wine.vintage || 'NM'}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-muted-foreground">Consommée le</p>
                      <p className="font-medium text-charcoal">
                        {new Date(wine.consumed_date).toLocaleDateString('fr-FR')}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Scan Dialog - Enhanced with front/back label support */}
      <Dialog open={showScanDialog} onOpenChange={(open) => {
        setShowScanDialog(open);
        if (!open) resetScanState();
      }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Camera className="w-5 h-5 text-red-600" />
              Scanner une bouteille
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {/* Step indicator */}
            <div className="flex items-center justify-center gap-2 text-sm">
              <div className={`flex items-center gap-1 ${scanStep === 1 ? 'text-red-600 font-medium' : 'text-muted-foreground'}`}>
                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs ${scanStep === 1 ? 'bg-red-600 text-white' : 'bg-gray-200'}`}>1</div>
                <span>Étiquette avant</span>
              </div>
              <ArrowRight className="w-4 h-4 text-muted-foreground" />
              <div className={`flex items-center gap-1 ${scanStep === 2 ? 'text-red-600 font-medium' : 'text-muted-foreground'}`}>
                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs ${scanStep === 2 ? 'bg-red-600 text-white' : 'bg-gray-200'}`}>2</div>
                <span>Dos (optionnel)</span>
              </div>
            </div>

            {/* Step 1: Front Label */}
            {scanStep === 1 && (
              <>
                <Card className="overflow-hidden">
                  <CardContent className="p-0">
                    {frontLabelImage ? (
                      <div className="relative">
                        <img 
                          src={frontLabelImage} 
                          alt="Étiquette avant" 
                          className="w-full aspect-[4/3] object-cover"
                        />
                        <Button
                          variant="ghost"
                          size="icon"
                          className="absolute top-2 right-2 bg-black/50 hover:bg-black/70 text-white rounded-full"
                          onClick={() => setFrontLabelImage(null)}
                        >
                          <X className="w-5 h-5" />
                        </Button>
                      </div>
                    ) : (
                      <label 
                        htmlFor="front-label-input"
                        className="aspect-[4/3] flex flex-col items-center justify-center bg-gradient-to-br from-red-50 to-rose-50 cursor-pointer hover:from-red-100 hover:to-rose-100 transition-colors"
                      >
                        <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mb-3">
                          <Wine className="w-8 h-8 text-red-600" />
                        </div>
                        <p className="font-medium text-charcoal">Photographier l'étiquette</p>
                        <p className="text-sm text-muted-foreground mt-1">ou sélectionner depuis la galerie</p>
                      </label>
                    )}
                    <input
                      id="front-label-input"
                      ref={frontInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleFrontImageUpload}
                      className="sr-only"
                    />
                  </CardContent>
                </Card>

                <div className="flex gap-2">
                  {!frontLabelImage ? (
                    <label 
                      htmlFor="front-label-input"
                      className="flex-1 h-12 rounded-xl bg-gradient-to-r from-red-600 to-rose-600 hover:from-red-700 hover:to-rose-700 text-white font-medium cursor-pointer flex items-center justify-center gap-2"
                    >
                      <Camera className="w-5 h-5" />
                      Prendre une photo
                    </label>
                  ) : (
                    <>
                      <Button
                        variant="outline"
                        onClick={() => setFrontLabelImage(null)}
                        className="flex-1"
                      >
                        <RotateCcw className="w-4 h-4 mr-2" />
                        Reprendre
                      </Button>
                      <Button
                        onClick={() => setScanStep(2)}
                        className="flex-1 bg-red-600 hover:bg-red-700"
                      >
                        Continuer
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    </>
                  )}
                </div>
              </>
            )}

            {/* Step 2: Back Label */}
            {scanStep === 2 && (
              <>
                <Card className="overflow-hidden">
                  <CardContent className="p-0">
                    {backLabelImage ? (
                      <div className="relative">
                        <img 
                          src={backLabelImage} 
                          alt="Dos de la bouteille" 
                          className="w-full aspect-[4/3] object-cover"
                        />
                        <Button
                          variant="ghost"
                          size="icon"
                          className="absolute top-2 right-2 bg-black/50 hover:bg-black/70 text-white rounded-full"
                          onClick={() => setBackLabelImage(null)}
                        >
                          <X className="w-5 h-5" />
                        </Button>
                      </div>
                    ) : (
                      <label 
                        htmlFor="back-label-input"
                        className="aspect-[4/3] flex flex-col items-center justify-center bg-gradient-to-br from-amber-50 to-orange-50 cursor-pointer hover:from-amber-100 hover:to-orange-100 transition-colors"
                      >
                        <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center mb-3">
                          <FileText className="w-8 h-8 text-amber-600" />
                        </div>
                        <p className="font-medium text-charcoal">Photographier le dos</p>
                        <p className="text-sm text-muted-foreground mt-1">Pour extraire la description du vin</p>
                        <p className="text-xs text-muted-foreground">(Optionnel)</p>
                      </label>
                    )}
                    <input
                      id="back-label-input"
                      ref={backInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleBackImageUpload}
                      className="sr-only"
                    />
                  </CardContent>
                </Card>

                {/* Tips */}
                <div className="bg-amber-50 rounded-lg p-3 border border-amber-200">
                  <p className="text-sm text-amber-800 flex items-start gap-2">
                    <Sparkles className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    Le dos de la bouteille contient souvent des notes de dégustation et des accords mets-vins que nous allons extraire pour vous.
                  </p>
                </div>

                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={() => setScanStep(1)}
                    className="flex-1"
                  >
                    Retour
                  </Button>
                  {!backLabelImage ? (
                    <label 
                      htmlFor="back-label-input"
                      className="flex-1 h-10 rounded-md bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-medium cursor-pointer flex items-center justify-center gap-2"
                    >
                      <Camera className="w-4 h-4" />
                      Photographier
                    </label>
                  ) : (
                    <Button
                      variant="outline"
                      onClick={() => setBackLabelImage(null)}
                    >
                      <RotateCcw className="w-4 h-4 mr-2" />
                      Reprendre
                    </Button>
                  )}
                </div>

                {/* Final action */}
                <Button
                  onClick={scanLabels}
                  disabled={scanning || !frontLabelImage}
                  className="w-full h-12 bg-gradient-to-r from-red-600 to-rose-600 hover:from-red-700 hover:to-rose-700"
                >
                  {scanning ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Analyse en cours...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-5 h-5 mr-2" />
                      Analyser {backLabelImage ? 'les étiquettes' : 'l\'étiquette'}
                    </>
                  )}
                </Button>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wine className="w-5 h-5" />
              Ajouter une bouteille
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Label Image Preview */}
            {formData.label_image && (
              <div className="w-32 h-32 rounded-lg overflow-hidden mx-auto">
                <img src={formData.label_image} alt="Étiquette" className="w-full h-full object-cover" />
              </div>
            )}

            {/* Wine Type */}
            <div>
              <label className="text-sm font-medium mb-2 block">Type de vin *</label>
              <div className="grid grid-cols-5 gap-2">
                {WINE_TYPES.map((type) => (
                  <button
                    key={type.value}
                    type="button"
                    onClick={() => setFormData({ ...formData, type: type.value })}
                    className={`p-2 rounded-lg text-center transition-all ${
                      formData.type === type.value
                        ? 'ring-2 ring-primary bg-primary/5'
                        : 'bg-gray-100 hover:bg-gray-200'
                    }`}
                  >
                    <span className="text-xl block">{type.icon}</span>
                    <span className="text-xs">{type.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Name & Domain */}
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium mb-1 block">Nom du vin *</label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Château Margaux"
                  required
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">Domaine *</label>
                <Input
                  value={formData.domain}
                  onChange={(e) => setFormData({ ...formData, domain: e.target.value })}
                  placeholder="Château Margaux"
                  required
                />
              </div>
            </div>

            {/* Appellation & Vintage */}
            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <label className="text-sm font-medium mb-1 block">Appellation</label>
                <Input
                  value={formData.appellation}
                  onChange={(e) => setFormData({ ...formData, appellation: e.target.value })}
                  placeholder="Margaux"
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">Millésime</label>
                <Input
                  type="number"
                  min="1900"
                  max="2030"
                  value={formData.vintage}
                  onChange={(e) => setFormData({ ...formData, vintage: e.target.value })}
                  placeholder="2018"
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">Degré d'alcool</label>
                <Input
                  type="number"
                  step="0.1"
                  value={formData.alcohol_content}
                  onChange={(e) => setFormData({ ...formData, alcohol_content: e.target.value })}
                  placeholder="13.5"
                />
              </div>
            </div>

            {/* Region & Country */}
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium mb-1 block">Région</label>
                <Input
                  value={formData.region}
                  onChange={(e) => setFormData({ ...formData, region: e.target.value })}
                  placeholder="Bordeaux"
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">Pays</label>
                <Input
                  value={formData.country}
                  onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                  placeholder="France"
                />
              </div>
            </div>

            {/* Purchase Info */}
            <div className="p-4 bg-gray-50 rounded-lg space-y-4">
              <h4 className="font-medium text-charcoal">Informations d'achat</h4>
              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <label className="text-sm font-medium mb-1 block">Date d'achat</label>
                  <Input
                    type="date"
                    value={formData.purchase_date}
                    onChange={(e) => setFormData({ ...formData, purchase_date: e.target.value })}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block">Prix (€)</label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.purchase_price}
                    onChange={(e) => setFormData({ ...formData, purchase_price: e.target.value })}
                    placeholder="25.00"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block">Quantité</label>
                  <Input
                    type="number"
                    min="1"
                    value={formData.quantity}
                    onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                  />
                </div>
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">Lieu d'achat</label>
                <Input
                  value={formData.purchase_location}
                  onChange={(e) => setFormData({ ...formData, purchase_location: e.target.value })}
                  placeholder="Cave du Marché, Paris"
                />
              </div>
            </div>

            {/* Notes */}
            <div>
              <label className="text-sm font-medium mb-1 block">Notes personnelles</label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Idéal pour un rôti de boeuf..."
                className="w-full p-3 border rounded-lg resize-none h-20"
              />
            </div>

            {/* Back Label Text - Description from back of bottle */}
            {formData.back_label_text && (
              <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                <div className="flex items-center gap-2 mb-2">
                  <FileText className="w-4 h-4 text-amber-600" />
                  <label className="text-sm font-medium text-amber-800">Description (dos de la bouteille)</label>
                </div>
                <p className="text-sm text-amber-900 whitespace-pre-wrap leading-relaxed">
                  {formData.back_label_text}
                </p>
              </div>
            )}

            {/* Actions */}
            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setShowAddDialog(false)}>
                Annuler
              </Button>
              <Button type="submit" disabled={saving}>
                {saving ? 'Ajout...' : 'Ajouter à la cave'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Wine Detail Dialog */}
      <Dialog open={!!selectedWine} onOpenChange={() => setSelectedWine(null)}>
        <DialogContent className="max-w-lg">
          {selectedWine && (
            <>
              <DialogHeader>
                <DialogTitle>{selectedWine.name}</DialogTitle>
              </DialogHeader>

              <div className="space-y-4">
                {/* Image */}
                {selectedWine.label_image && (
                  <div className="w-full h-48 rounded-lg overflow-hidden">
                    <img src={selectedWine.label_image} alt={selectedWine.name} className="w-full h-full object-cover" />
                  </div>
                )}

                {/* Info */}
                <div className="space-y-2">
                  <p className="text-lg font-semibold text-charcoal">{selectedWine.domain}</p>
                  <div className="flex flex-wrap gap-2">
                    <span className="bg-gray-100 px-2 py-1 rounded text-sm">
                      {WINE_TYPES.find(t => t.value === selectedWine.type)?.label || selectedWine.type}
                    </span>
                    {selectedWine.vintage && (
                      <span className="bg-gray-100 px-2 py-1 rounded text-sm">{selectedWine.vintage}</span>
                    )}
                    {selectedWine.appellation && (
                      <span className="bg-gray-100 px-2 py-1 rounded text-sm">{selectedWine.appellation}</span>
                    )}
                  </div>
                </div>

                {/* Purchase Info */}
                <div className="p-4 bg-gray-50 rounded-lg space-y-2">
                  {selectedWine.purchase_date && (
                    <p className="text-sm flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-muted-foreground" />
                      Acheté le {new Date(selectedWine.purchase_date).toLocaleDateString('fr-FR')}
                    </p>
                  )}
                  {selectedWine.purchase_location && (
                    <p className="text-sm flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-muted-foreground" />
                      {selectedWine.purchase_location}
                    </p>
                  )}
                  {selectedWine.purchase_price && (
                    <p className="text-sm font-medium">Prix: {selectedWine.purchase_price}€</p>
                  )}
                  <p className="text-sm font-medium">Quantité en cave: {selectedWine.quantity}</p>
                </div>

                {selectedWine.notes && (
                  <div className="p-4 bg-amber-50 rounded-lg">
                    <p className="text-sm text-amber-800">{selectedWine.notes}</p>
                  </div>
                )}

                {/* Back Label Description */}
                {selectedWine.back_label_text && (
                  <div className="p-4 bg-gradient-to-br from-amber-50 to-orange-50 rounded-lg border border-amber-200">
                    <div className="flex items-center gap-2 mb-2">
                      <FileText className="w-4 h-4 text-amber-600" />
                      <span className="text-sm font-medium text-amber-800">Description du vin</span>
                    </div>
                    <p className="text-sm text-amber-900 whitespace-pre-wrap leading-relaxed">
                      {selectedWine.back_label_text}
                    </p>
                  </div>
                )}

                {/* Actions */}
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    className="flex-1 text-red-500" 
                    onClick={() => deleteWine(selectedWine.id)}
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Supprimer
                  </Button>
                  <Button 
                    className="flex-1"
                    onClick={() => consumeWine(selectedWine.id, 1)}
                  >
                    <GlassWater className="w-4 h-4 mr-2" />
                    Consommer
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
